import { useCallback, useState } from 'react';
import { useSendbirdContext } from '../SendbirdProviderWithSDK';
import { Plus, Search, Loader2 } from 'lucide-react';
import { useCreateChannelContext } from '@sendbird/uikit-react/CreateChannel/context';
import { useHasPermission } from '../../../auth/useHasPermission';
import { CHANNEL_TYPE, type SearchMode } from './types';
import { SelectCreateGroupsModal } from './SelectCreateGroupsModal';
import { CreateGroupsModal } from './CreateGroupsModal';
import { SearchModePopover } from './SearchModePopover';
import { useSearchControl } from './SearchControlContext';
import { useTranslate } from 'ra-core';
import { Avatar, AvatarImage } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

export const GroupChannelListHeaderMiddle = () => {
  const { searchMode, setSearchMode, searchTerm, setSearchTerm } =
    useSearchControl();

  const currentUser = useSendbirdContext().sb?.currentUser;
  const isAdmin = useHasPermission('ADMIN_ACCESS');
  const { setType } = useCreateChannelContext();
  const translate = useTranslate();

  const searchModes: SearchMode[] = [
    { mode: 'channel', label: translate('chat.searchModes.channelName') },
    {
      mode: 'employee',
      label: translate('chat.searchModes.username'),
    },
  ];

  const [openCreateGroups, setOpenCreateGroups] = useState(false);
  const [openSelectGroupType, setOpenSelectGroupType] = useState(false);

  const handleCreateChannel = useCallback(() => {
    if (isAdmin) {
      setOpenSelectGroupType(true);
    } else {
      setType(CHANNEL_TYPE.GROUP);
      setOpenCreateGroups(true);
    }
  }, [isAdmin, setType]);

  if (!currentUser) {
    return <Loader2 className="self-center animate-spin text-primary" />;
  }

  return (
    <>
      <div className="w-full min-w-0">
        <div className="flex items-center justify-between">
          {/* Avatar and Name */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Avatar className="size-12 shrink-0">
              <AvatarImage
                src={currentUser.profileUrl}
                alt={currentUser.nickname}
              />
            </Avatar>

            <Tooltip>
              <TooltipTrigger className="min-w-0 truncate text-left text-sm">
                {currentUser.nickname}
              </TooltipTrigger>
              <TooltipContent>{currentUser.nickname}</TooltipContent>
            </Tooltip>
          </div>

          {/* Create channel button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate('chat.createNewGroup')}
            onClick={handleCreateChannel}
          >
            <Plus />
          </Button>
        </div>

        {/* Search field for channel names, with Popover select search mode */}
        <div className="relative mt-2">
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={translate('chat.search')}
            className="h-10 pl-24 pr-9"
          />
          <SearchModePopover
            searchMode={searchMode}
            setSearchMode={setSearchMode}
            searchModes={searchModes}
          />
          <Search className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      {/* Create channels modals */}
      {isAdmin && (
        <SelectCreateGroupsModal
          open={openSelectGroupType}
          onClose={() => setOpenSelectGroupType(false)}
        />
      )}
      {!isAdmin && (
        <CreateGroupsModal
          open={openCreateGroups}
          onClose={() => setOpenCreateGroups(false)}
        />
      )}
    </>
  );
};
